import {
  formatPrice,
  fetchProducts,
} from '~js/utilities/products-and-collections';
const optionsToHide = Object.freeze(['Color', 'Set']);
const forceManualOption = Object.freeze(['Size']);

const { PRODUCT_STATES_CONFIG } = window;
const matcher = window.matchMedia(`(min-width: 1024px)`);

export const productData = (product, algoliaQueryID) => ({
  algoliaQueryID: algoliaQueryID,
  product,
  options: product.options,
  selectedVariants: product.variants,
  selectedOptions: Object.fromEntries(
    product.options.map((option) => [option.name, ''])
  ),
  isWorking: false,
  drawerSide: '',
  gwpProductFeature: null,
  collectionGwp: null,
  collectionGwpProduct: null,

  fetchProduct() {
    const ids = [
      window.COLLECTION_GWP_PRODUCT_ID
    ].filter(Boolean).filter(id => id !== 0);
    fetchProducts(ids)
      .then((products) => {
        this.collectionGwpProduct = products[window.COLLECTION_GWP_PRODUCT_ID];
      })
      .catch((e) => {
        this.error = e.message;
      });
  },

  get optionsToHide() {
    const totalVariants = product.variants.length;

    return Object.fromEntries(
      this.options
        .filter(
          (option) =>
            (totalVariants === 1 && !forceManualOption.includes(option.name)) ||
            (optionsToHide.includes(option.name) &&
              option.values.length === 1 &&
              this.selectedOptions[option.name] === option.values[0])
        )
        .map((option) => [option.name, true])
    );
  },
  get collectionGwp(){
    if(this.product.tags.length > 0){
      this.fetchProduct()
      return Object.values(this.product.tags).includes('Holiday Shoppe')
    }
    return false
  },
  get collectionGwpAvailable(){
    if(this.collectionGwpProduct){
      return this.collectionGwpProduct?.availableForSale && this.collectionGwpProduct?.gwpProductAmount?.value ? this.collectionGwpProduct?.gwpProductAmount?.value > 0 : false;
    }
    return false;
  },
  get collectionGwpPdpCopy(){
    const message =  window.COLLECTION_GWP_PDP_COPY || '';
    const amount = this.collectionGwpProduct?.gwpProductAmount?.value || '';
    const result = message.replace("{amount}", amount);
    return result;
  },
  get collectionGwpEnable(){ 
    return window.COLLECTION_GWP_ENABLE || false;
  },
  get collectionGwpProductImage(){
    return this.collectionGwpProduct?.images?.nodes[0] || null;
  },
  get gwpFeatureEnable(){
    return window.GWP_FEATURE_ENABLE || false;
  },
  get gwpProductFeatureAvailable(){
    if(this.gwpProductFeature){
      return this.product.gwpProduct?.reference?.availableForSale;
    }
    return false;
  },
  get gwpProductFeatureImage(){
    return this.product.gwpProduct?.reference?.product?.images?.nodes[0] || null;
  },
  get gwpProductFeature(){
    return this.product.gwpProduct?.reference || null;
  },
  get gwpProductFeatureMessage(){
    return this.product?.gwpProductMessage !== null ? this.product?.gwpProductMessage?.value : this.gwpProductFeature.product.title;
  },
  get gwpProductFeaturePrice(){
    if(this.product.gwpProduct?.reference?.price && this.product.gwpProduct?.reference?.price?.currencyCode){
      return formatPrice( {amount: this.product.gwpProduct?.reference?.price?.amount, currencyCode: this.product.gwpProduct?.reference?.price?.currencyCode }, false);
    }
    return ''
  },

  get visibleOptions() {
    const { options, optionsToHide } = this;
    const optionsToHideKeys = Object.keys(optionsToHide);
    return options.filter((option) => !optionsToHideKeys.includes(option.name));
  },

  get optionsWithState() {
    const { options, selectedOptions } = this;

    return options.map((option) => {
      const { name, values } = option;
      return {
        name,
        values: values.map((value) => {
          const newSelectedOptions = { ...selectedOptions };
          delete newSelectedOptions[name];
          const newSelectedOptionsArray = [
            [name, value],
            ...Object.entries(newSelectedOptions),
          ];
          const { availableVariants } = this.calculateOptionsAndVariants(
            newSelectedOptionsArray
          );
          return {
            value,
            variants: availableVariants.length,
            variantsInStock: availableVariants.filter(
              (variant) => variant.availableForSale
            ).length,
          };
        }),
      };
    });
  },

  get willRestock() {
    return (
      product.seasonalCore?.value === 'Core' ||
      product.tags.includes('will-restock')
    );
  },

  get isComingSoon() {
    return product.tags.includes('Coming Soon');
  },

  get isBackorderTaggedProduct() {
    return product.tags.includes('backorder');
  },

  get isAppExclusive() {
    return product.tags.includes('app-exclusive');
  },

  get fit() {
    return product.fit?.value;
  },

  // coming-soon, many-variants, sold-out-permanently, sold-out-notify, available
  get stateBase() {
    const { availableForSale } = product;

    if (this.isAppExclusive) {
      return 'app-exclusive';
    }

    if (this.isComingSoon &&
      ((!availableForSale && (!window.localStorage.getItem("isVIP") || !window.VIP_SHOP_EARLY_STATE?.state || !product.tags.includes('vip-shop-early'))) ||
       (availableForSale && (!window.localStorage.getItem("isVIP") && window.VIP_SHOP_EARLY_STATE?.state && product.tags.includes('vip-shop-early'))))) {
      return 'coming-soon';
    }

    const { selectedVariants } = this;

    const [selectedVariant] = selectedVariants;

    if (selectedVariants.length > 1) {
      return 'many-variants';
    }

    // Two cases for showing Backorder copy for ATC button
    if (selectedVariant.currentlyNotInStock) {
      if (product.tags.includes('backorder')) {
        return 'back-order';
      }
      return 'pre-order';
    }
    else {
      if (product.tags.includes('backorder') && selectedVariant.isBackorder?.value == 'true') {
        return 'back-order';
      }
    }

    const { willRestock } = this;

    if (!availableForSale) {
      return `sold-out-${willRestock ? 'notify' : 'permanently'}`;
    }

    if (!selectedVariant.availableForSale) {
      return `sold-out-${willRestock ? 'notify' : 'permanently'}`;
    }

    return 'available';
  },

  get buttonLabelBase() {
    if (this.isWorking) return 'Working…';

    const { stateBase } = this;

    switch (stateBase) {
      case 'coming-soon':
      case 'sold-out-notify':
        return PRODUCT_STATES_CONFIG[stateBase].buttonLabel;
      case 'many-variants': {
        const missingOptions = Object.entries(this.selectedOptions)
          .filter(([, value]) => value === '')
          .map(([name]) => name);
        return `Select ${
          missingOptions.length === 1 ? 'a ' : ''
        }${missingOptions.join(', ')}`;
      }
      case 'sold-out-permanently':
        return 'Sold Out';
      case 'pre-order':
        return 'Pre-Order';
      case 'back-order':
        return 'Backorder';
      case 'available':
        return 'Add to Cart';
      case 'app-exclusive':
        return 'Download App';
      default:
        return '';
    }
  },

  get variantPdpCopy() {
    const { selectedVariants, product } = this;
    const [selectedVariant] = selectedVariants;

    return selectedVariant.pdpCopy?.value? selectedVariant.pdpCopy.value : null;
  },

  get isButtonDisabled() {
    return this.stateBase === 'sold-out-permanently';
  },

  get sizeGuideType() {
    return product.size_guide_type?.value;
  },

  init() {
    this.normalizeOptionsAndVariants(Object.entries(this.selectedOptions));
  },

  calculateOptionsAndVariants(selectedOptionsArray) {
    let availableVariants = product.variants;
    const correctSelectedOptions = {};

    for (const [name, value] of selectedOptionsArray) {
      const previousVariants = availableVariants;
      availableVariants = availableVariants.filter((variant) =>
        variant.selectedOptions.some(
          (option) => option.name === name && option.value === value
        )
      );

      if (availableVariants.length === 0) {
        availableVariants = previousVariants;
        if (forceManualOption.includes(name) && availableVariants.length > 1) {
          correctSelectedOptions[name] = '';
        } else {
          const newValue = availableVariants[0].selectedOptions.find(
            (option) => option.name === name
          ).value;
          correctSelectedOptions[name] = newValue;
          availableVariants = availableVariants.filter((variant) =>
            variant.selectedOptions.some(
              (option) => option.name === name && option.value === newValue
            )
          );
        }
      } else {
        correctSelectedOptions[name] = value;
      }
    }

    return { availableVariants, correctSelectedOptions };
  },

  normalizeOptionsAndVariants(selectedOptionsArray) {
    const { availableVariants, correctSelectedOptions } =
      this.calculateOptionsAndVariants(selectedOptionsArray);

    this.selectedOptions = correctSelectedOptions;
    this.selectedVariants = availableVariants;

    const event = new CustomEvent('product-variant-selection-change', {
      detail: {
        skus: this.selectedVariants.map((variant) => variant.sku),
        selectedOptions: this.selectedOptions,
      },
    });

    window.dispatchEvent(event);
  },

  handleOptionChange(name, value) {
    const newSelectedOptions = { ...this.selectedOptions };
    delete newSelectedOptions[name];
    const newSelectedOptionsArray = [
      [name, value],
      ...Object.entries(newSelectedOptions),
    ];
    this.normalizeOptionsAndVariants(newSelectedOptionsArray);
  },

  async addToCart(props = {}) {
    if (this.isWorking) return;

    const {
      isFacebook = false,
      disableCartDrawer = window.CURRENT_TEMPLATE === 'cart',
      lineItemProperties = {},
     } = props;
    const { selectedVariants, product } = this;
    const [selectedVariant] = selectedVariants;
    const simpleId = selectedVariant.id.replace(
      'gid://shopify/ProductVariant/',
      ''
    );
    const simpleProductId = product.id.replace(
      'gid://shopify/Product/',
      ''
    );
    const promoGWP = this.product.gwpProduct?.value  ? this.product.gwpProduct?.value.replace(
      'gid://shopify/ProductVariant/',
      ''
    ) : null;

    const promoGWPAvailable = this.product.gwpProduct?.reference?.availableForSale;

    try {
      this.isWorking = true;
      const country_available_to_sell = product.availableCountryToSell?.value;
      const properties = {
        ...lineItemProperties,
        'properties': { '__country_available_to_sell': country_available_to_sell }
      }
      if (isFacebook) {
        properties['__Facebook'] = 'VIP';
      }
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'post',
        body: JSON.stringify({ items: [{ id: simpleId,  properties}].concat(promoGWPAvailable && promoGWP && window.GWP_FEATURE_ENABLE ? [{ id: promoGWP, quantity: 1, properties: { __isFreeGift: '__isFreeGift', __productIdRequired: simpleProductId } }] : []) }),
        headers: { 'content-type': 'application/json' },
      });

      if (!res.ok) {
        let description = '';
        try {
          const data = await res.json();
          description = data?.description;
        } catch (e) {
          //
        }
        throw new Error(
          [`Failed to add to cart!`, description, `(status: ${res.status})`]
            .filter(Boolean)
            .join('\n')
        );
      }
      else {
        // Klaviyo Add To Cart event
        let item = {
          Brand: 'Posh Peanut',
          CompareAtPrice: selectedVariant.compareAtPrice ? '$' + selectedVariant.compareAtPrice : '$0.00',
          ImageURL: product.images?.nodes[0]?.url,
          Name: product.title,
          Price: '$' + selectedVariant.price.amount,
          ProductID: simpleProductId,
          URL: product.onlineStoreUrl,
          VariantID: simpleId
        }

        var _learnq = window._learnq || [];
        _learnq.push(['track', 'Added to Cart (Posh Peanut Report)', item]);
        // End Klaviyo Add To Cart Event

        // Magellan Add to cart event
        var MAI = window.MAI;
        MAI.emit('addToCart', selectedVariant.price.amount, "USD", 1, {
          productId: item.ProductID,
          productName: item.Name,
          variantId: item.VariantID
        });
      }

      this.$dispatch('update-and-open-cart', {
        noOpenAfterUpdate: disableCartDrawer,
      });
    } catch (error) {
      alert(error.message);
      this.isWorking = false;
      throw error;
    }
  },

  handleSizeGuideClick(e) {
    this.drawerSide = matcher.matches ? 'right' : 'bottom';

    e.target
      .closest('button')
      .parentElement.querySelector('dialog')
      .showModal();
  },
});
